import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { tenantApi, type SubscriptionStatusType } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

interface TenantInfo {
    name: string;
    slug: string;
    city?: string;
    state?: string;
}

interface TenantContextType {
    tenant: TenantInfo | null;
    tenantSlug: string | null;
    loading: boolean;
    isSuperAdmin: boolean;
    setSuperAdmin: (val: boolean) => void;
    isPlatformSubdomain: boolean;
    isRootDomain: boolean;
    subscription: SubscriptionStatusType | null;
    isSubscriptionActive: boolean;
}

const TenantContext = createContext<TenantContextType>({
    tenant: null,
    tenantSlug: null,
    loading: true,
    isSuperAdmin: false,
    setSuperAdmin: () => {},
    isPlatformSubdomain: false,
    isRootDomain: false,
    subscription: null,
    isSubscriptionActive: true,
});

export function useTenant() {
    return useContext(TenantContext);
}

function detectTenantSlug(): { slug: string | null; isPlatform: boolean; isRoot: boolean } {
    const params = new URLSearchParams(window.location.search);
    const slugParam = params.get('tenant');
    if (slugParam) return { slug: slugParam, isPlatform: false, isRoot: false };

    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const isLabfloDomain = hostname.endsWith('.labflo.org') || hostname === 'labflo.org';

    if (isLabfloDomain && parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'app') {
        const subdomain = parts[0];
        if (subdomain === 'platform' || subdomain === 'admin') {
            return { slug: null, isPlatform: true, isRoot: false };
        }
        return { slug: subdomain, isPlatform: false, isRoot: false };
    }

    if (hostname === 'labflo.org' || hostname === 'www.labflo.org') {
        return { slug: null, isPlatform: false, isRoot: true };
    }

    const stored = sessionStorage.getItem('tenant_slug');
    if (stored) return { slug: stored, isPlatform: false, isRoot: false };

    return { slug: null, isPlatform: false, isRoot: false };
}

export function TenantProvider({ children }: { children: ReactNode }) {
    const location = useLocation();
    const [tenant, setTenant] = useState<TenantInfo | null>(null);
    const [isPlatformSubdomain] = useState(() => detectTenantSlug().isPlatform);
    const [isRootDomain] = useState(() => detectTenantSlug().isRoot);
    const [tenantSlug, setTenantSlug] = useState<string | null>(() => {
        const { slug } = detectTenantSlug();
        if (slug) {
            axios.defaults.headers.common['X-Tenant-Slug'] = slug;
        }
        return slug;
    });
    const [loading, setLoading] = useState(true);
    const [isSuperAdmin, setSuperAdmin] = useState(false);
    const [subscription, setSubscription] = useState<SubscriptionStatusType | null>(null);

    // Subscription is considered active if: status is 'active', OR no subscription data yet (default allow),
    // OR status is 'none' (super admin hasn't set one = allow access for now)
    const isSubscriptionActive =
        !subscription ||
        subscription.status === 'active' ||
        subscription.status === 'none' ||
        subscription.status == null;

    useEffect(() => {
        const { slug } = detectTenantSlug();
        setTenantSlug((prev) => (prev === slug ? prev : slug));
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!tenantSlug) {
            delete axios.defaults.headers.common['X-Tenant-Slug'];
            setLoading(false);
            return;
        }

        sessionStorage.setItem('tenant_slug', tenantSlug);
        axios.defaults.headers.common['X-Tenant-Slug'] = tenantSlug;

        // Resolve tenant info
        Promise.all([
            axios.get(`${API}/api/super-admin/resolve/${tenantSlug}`)
                .then(res => setTenant(res.data))
                .catch(() => {
                    if (tenantSlug === 'default') {
                        setTenant({ name: 'Default Pharmacy', slug: 'default' });
                    }
                }),
            tenantApi.subscriptionStatus(tenantSlug)
                .then(sub => setSubscription(sub))
                .catch(() => setSubscription(null)),
        ]).finally(() => setLoading(false));
    }, [tenantSlug]);

    return (
        <TenantContext.Provider value={{
            tenant,
            tenantSlug,
            loading,
            isSuperAdmin,
            setSuperAdmin,
            isPlatformSubdomain,
            isRootDomain,
            subscription,
            isSubscriptionActive,
        }}>
            {children}
        </TenantContext.Provider>
    );
}
